import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSpinner, faSave } from "@fortawesome/free-solid-svg-icons";
import { utilsService, DniTypeObject, applyDniPrefix, getMaxLengthFromMask, getDniCompositeKey, findDniTypeByCompositeKey } from "../../services/utils.service";
import { countryService, CountryFull } from "../../services/country.service";
import {
    customerAddressService,
    Address,
} from "../../services/customer.address.service";
import useForm from "../../hooks/useForm";
import { tokenService } from "../../services/token.service";
import Swal from "sweetalert2";
import AddressAutocomplete, { AddressComponents } from "./AddressAutocomplete";
import styles from "../../styles/common/EditAddressModal.module.css";
import modalStyles from "../../styles/common/Modal.module.css";
import { formatPhoneNational, isValidPhone, getPhonePlaceholder } from "../../config/phone.utils";
import { normalizeZip, isValidZipFormat, getZipFormatErrorMessage } from "../../config/zip.utils";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

interface EditAddressModalProps {
    isOpen: boolean;
    onClose: () => void;
    address: Address | null;
    onSuccess: () => void;
}

const EditAddressModal: React.FC<EditAddressModalProps> = ({
    isOpen,
    onClose,
    address,
    onSuccess,
}) => {
    const activeType = address?.type === "D" ? "destination" : "origin";
    const [selectedCountry, setSelectedCountry] = useState<string>("US");
    const [countries, setCountries] = useState<CountryFull[]>([]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await countryService.getAllCountries();
                setCountries(data);
            } catch (err) {
                console.error("Error fetching countries:", err);
            }
        };
        fetchCountries();
    }, []);

    const selectedCountryObj = React.useMemo(
        () => countries.find((c) => c.iso2Country === selectedCountry) ?? null,
        [countries, selectedCountry]
    );

    const country = selectedCountry;

    const [isValidatingZip, setIsValidatingZip] = useState(false);
    const [zipValidated, setZipValidated] = useState(true); // Default to true since it exists
    const [zipCodeApiError, setZipCodeApiError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [dniTypes, setDniTypes] = useState<DniTypeObject[]>([]);
    const [loadingDniTypes, setLoadingDniTypes] = useState(false);
    const [backupOwnerData, setBackupOwnerData] = useState<{
        firstName: string;
        lastName: string;
        kindOfDNI: string;
        dni: string;
        email: string;
        phoneNumber: string;
    } | null>(null);

    const [backupThirdPersonData, setBackupThirdPersonData] = useState<{
        firstName: string;
        lastName: string;
        kindOfDNI: string;
        dni: string;
        email: string;
        phoneNumber: string;
    } | null>(null);

    const {
        values,
        handleChange,
        errors,
        validate,
        isValid,
        setValues,
        setErrors,
    } = useForm(
        {
            nickname: "",
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zip: "",
            isDefault: false,
            // Subject fields
            firstName: "",
            lastName: "",
            kindOfDNI: "",
            dni: "",
            phoneNumber: "",
            email: "",
            isThirdPerson: false,
            razonSocial: "",
        },
        {
            nickname: (value) => (value ? null : "El alias es requerido."),
            addressLine1: (value) => (value ? null : "La dirección es requerida."),
            city: (value) => (value ? null : "La ciudad es requerida."),
            state: (value) => (value ? null : "El estado es requerido."),
            zip: (value) => (value ? null : "El código postal es requerido."),
            phoneNumber: (value) => {
                if (!value) return "El teléfono es requerido.";
                if (!isValidPhone(value, selectedCountry)) return "Número de teléfono inválido.";
                return null;
            },
            firstName: (value) => (value ? null : "El nombre es requerido."),
            lastName: (value) => (value ? null : "El apellido es requerido."),
            kindOfDNI: (value) => value ? null : "El tipo de documento es requerido.",
            dni: (value, allValues) => {
                if (!value) return "El documento es requerido.";
                const selectedDniObj = findDniTypeByCompositeKey(dniTypes, allValues.kindOfDNI);
                if (selectedDniObj && selectedDniObj.mask) {
                    const regex = new RegExp(selectedDniObj.mask);
                    if (!regex.test(value)) {
                        return `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
                    }
                }
                return null;
            },
            email: (value) => {
                if (!value) return "El email es requerido.";
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) return "Email inválido.";
                return null;
            },
            razonSocial: (value, allValues) => {
                const selectedDniObj = findDniTypeByCompositeKey(dniTypes, allValues.kindOfDNI);
                if (selectedDniObj?.requiereRazonSocial === true && !value) {
                    return "La razón social es requerida.";
                }
                return null;
            },
        },
    );

    useEffect(() => {
        let isSubscribed = true;
        const fetchDniTypes = async () => {
            if (isOpen && selectedCountry) {
                setLoadingDniTypes(true);
                setDniTypes([]);
                try {
                    const data = await utilsService.getDniTypes(selectedCountry);
                    if (isSubscribed) {
                        setDniTypes(data);

                        // Mapear kindOfDNI de la dirección actual a su composite key respectivo
                        if (address && address.addressSubjet?.kindOfDNI) {
                            const matched = data.find(t => t.kindOfDNI === address.addressSubjet?.kindOfDNI || getDniCompositeKey(t) === address.addressSubjet?.kindOfDNI);
                            if (matched) {
                                const compositeKey = getDniCompositeKey(matched);
                                setValues(prev => ({
                                    ...prev,
                                    kindOfDNI: compositeKey
                                }));
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error fetching DNI types:", error);
                    if (isSubscribed) setDniTypes([]);
                } finally {
                    if (isSubscribed) setLoadingDniTypes(false);
                }
            } else {
                if (isSubscribed) {
                    setDniTypes([]);
                    setLoadingDniTypes(false);
                }
            }
        };
        fetchDniTypes();
        return () => {
            isSubscribed = false;
        };
    }, [isOpen, selectedCountry, address]);

    // Sync state with address prop
    useEffect(() => {
        if (isOpen && address) {
            const countryIso = address.isO2Country || (address.type === "D" ? "VE" : "US");
            setSelectedCountry(countryIso);

            // Find country code to strip it from phone and format it
            const rawPhone = address.addressSubjet?.phoneNumber || "";
            const countryObj = countries.find((c) => c.iso2Country === countryIso);
            const formattedPhone = formatPhoneNational(rawPhone, countryIso, countryObj?.phoneCode);

            setValues({
                nickname: address.nickname || "",
                addressLine1: address.addressLine1 || "",
                addressLine2: address.addressLine2 || "",
                city: address.city || "",
                state: address.state || "",
                zip: address.zipCode || "",
                isDefault: !!address.isDefault,
                firstName: address.addressSubjet?.firstName || "",
                lastName: address.addressSubjet?.lastName || "",
                kindOfDNI: address.addressSubjet?.kindOfDNI || "",
                dni: address.addressSubjet?.dni || "",
                phoneNumber: formattedPhone,
                email: address.addressSubjet?.email || "",
                isThirdPerson: !!address.addressSubjet?.isThirdPerson,
                razonSocial: address.addressSubjet?.razonSocial || "",
            });
            const initialSubject = {
                firstName: address.addressSubjet?.firstName || "",
                lastName: address.addressSubjet?.lastName || "",
                kindOfDNI: address.addressSubjet?.kindOfDNI || "",
                dni: address.addressSubjet?.dni || "",
                phoneNumber: formattedPhone,
                email: address.addressSubjet?.email || "",
            };

            if (address.addressSubjet?.isThirdPerson) {
                setBackupThirdPersonData(initialSubject);
                // Pre-populate backupOwnerData with tokenData if available
                const tokenData = tokenService.getCustomerDataToken()?.customerData;
                if (tokenData && tokenData.ISO2Country === "US") {
                    const countryObj = countries.find((c) => c.iso2Country === "US");
                    const formattedTokenPhone = formatPhoneNational(
                        tokenData.PhoneNumber || "",
                        "US",
                        countryObj?.phoneCode || "1"
                    );
                    const matchedDni = dniTypes.find(t => t.kindOfDNI === tokenData.KindOfDNI || getDniCompositeKey(t) === tokenData.KindOfDNI);
                    const compositeKey = matchedDni ? getDniCompositeKey(matchedDni) : "";
                    setBackupOwnerData({
                        firstName: tokenData.FirstName || "",
                        lastName: tokenData.LastName || "",
                        kindOfDNI: compositeKey,
                        dni: tokenData.DNI || "",
                        email: tokenData.Email || "",
                        phoneNumber: formattedTokenPhone || "",
                    });
                } else {
                    setBackupOwnerData(null);
                }
            } else {
                setBackupOwnerData(initialSubject);
                setBackupThirdPersonData(null);
            }
            setErrors({});
            setZipValidated(true);
            setZipCodeApiError(null);
        }
    }, [isOpen, address, countries, setValues, setErrors]);

    const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(e);
        setZipValidated(false);
        setZipCodeApiError(null);
    };

    const handleAddressSelect = (addressData: AddressComponents) => {
        const normalizedZip = normalizeZip(addressData.zipCode || "", country);
        setValues((prev) => ({
            ...prev,
            addressLine1: addressData.fullAddress,
            city: addressData.city || prev.city,
            state: addressData.state || prev.state,
            zip: normalizedZip || prev.zip,
        }));
        setErrors((prev) => ({
            ...prev,
            addressLine1: undefined,
            city: undefined,
            state: undefined,
            zip: undefined,
        }));
        setZipCodeApiError(null);
        setZipValidated(true);
    };

    const validateModalZip = async (zipToValidate?: string) => {
        const rawZip = zipToValidate !== undefined ? zipToValidate : values.zip;
        if (!rawZip) {
            setZipCodeApiError("El código postal es requerido");
            setZipValidated(false);
            return null;
        }
        const zip = normalizeZip(rawZip, country);

        if (!isValidZipFormat(zip, country)) {
            setZipCodeApiError(getZipFormatErrorMessage(country));
            setZipValidated(false);
            return null;
        }

        setZipCodeApiError(null);
        setZipValidated(true);
        setIsValidatingZip(true);

        try {
            const location = await utilsService.getLocationByZipcode({
                iso2code: country,
                zipcode: zip,
            });

            const newCity = location?.city || values.city;
            const newState = location?.state || values.state;
            const updatedFields = {
                city: newCity,
                state: newState,
                zip: zip,
            };
            setErrors((prevErrors) => ({
                ...prevErrors,
                city: newCity ? undefined : prevErrors.city,
                state: newState ? undefined : prevErrors.state,
                zip: undefined,
            }));
            setValues((prev) => ({
                ...prev,
                ...updatedFields,
            }));
            return updatedFields;
        } catch (error) {
            console.warn("Non-blocking zip lookup notice:", error);
            const updatedFields = {
                city: values.city,
                state: values.state,
                zip: zip,
            };
            return updatedFields;
        } finally {
            setIsValidatingZip(false);
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const formatted = formatPhoneNational(val, selectedCountry, selectedCountryObj?.phoneCode);
        setValues((prev) => ({
            ...prev,
            phoneNumber: formatted,
        }));

        const isVal = isValidPhone(formatted, selectedCountry);
        setErrors((prev) => ({
            ...prev,
            phoneNumber: !formatted ? "El teléfono es requerido." : !isVal ? "Número de teléfono inválido." : undefined,
        }));
    };

    const handleDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.kindOfDNI);
        const formatted = applyDniPrefix(val, selectedDniObj?.mask);
        setValues((prev) => ({
            ...prev,
            dni: formatted,
        }));

        let error: string | null = null;
        if (!formatted) {
            error = "El documento es requerido.";
        } else if (selectedDniObj && selectedDniObj.mask) {
            const regex = new RegExp(selectedDniObj.mask);
            if (!regex.test(formatted)) {
                error = `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
            }
        }
        setErrors((prev) => ({
            ...prev,
            dni: error || undefined,
        }));
    };

    const handleKindOfDniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setValues((prev) => ({
            ...prev,
            kindOfDNI: val,
            dni: "",
        }));
        setErrors((prev) => ({
            ...prev,
            kindOfDNI: undefined,
            dni: undefined,
        }));
    };

    const handleIsThirdPersonToggle = (isChecked: boolean) => {
        const tokenData = tokenService.getCustomerDataToken()?.customerData;
        const countryIso = "US";

        const currentSubjectData = {
            firstName: values.firstName,
            lastName: values.lastName,
            kindOfDNI: values.kindOfDNI,
            dni: values.dni,
            email: values.email,
            phoneNumber: values.phoneNumber,
        };
        
        if (isChecked) {
            // Save current (Owner) data before toggling
            setBackupOwnerData(currentSubjectData);

            // Load Third Person data
            if (backupThirdPersonData) {
                setValues((prev: any) => ({
                    ...prev,
                    isThirdPerson: true,
                    firstName: backupThirdPersonData.firstName,
                    lastName: backupThirdPersonData.lastName,
                    kindOfDNI: backupThirdPersonData.kindOfDNI,
                    dni: backupThirdPersonData.dni,
                    email: backupThirdPersonData.email,
                    phoneNumber: backupThirdPersonData.phoneNumber,
                }));
            } else {
                setValues((prev: any) => ({
                    ...prev,
                    isThirdPerson: true,
                    firstName: "",
                    lastName: "",
                    kindOfDNI: "",
                    dni: "",
                    email: "",
                    phoneNumber: "",
                }));
            }
        } else {
            // Save current (Third Person) data before toggling
            setBackupThirdPersonData(currentSubjectData);

            // Load Owner data
            if (backupOwnerData) {
                setValues((prev: any) => ({
                    ...prev,
                    isThirdPerson: false,
                    firstName: backupOwnerData.firstName,
                    lastName: backupOwnerData.lastName,
                    kindOfDNI: backupOwnerData.kindOfDNI,
                    dni: backupOwnerData.dni,
                    email: backupOwnerData.email,
                    phoneNumber: backupOwnerData.phoneNumber,
                }));
            } else if (tokenData && tokenData.ISO2Country === countryIso) {
                const countryObj = countries.find((c) => c.iso2Country === "US");
                const formattedPhone = formatPhoneNational(
                    tokenData.PhoneNumber || "",
                    "US",
                    countryObj?.phoneCode || "1"
                );
                const matchedDni = dniTypes.find(t => t.kindOfDNI === tokenData.KindOfDNI || getDniCompositeKey(t) === tokenData.KindOfDNI);
                const compositeKey = matchedDni ? getDniCompositeKey(matchedDni) : "";

                setValues((prev: any) => ({
                    ...prev,
                    isThirdPerson: false,
                    firstName: tokenData.FirstName || "",
                    lastName: tokenData.LastName || "",
                    kindOfDNI: compositeKey,
                    dni: tokenData.DNI || "",
                    email: tokenData.Email || "",
                    phoneNumber: formattedPhone || "",
                }));
            } else {
                setValues((prev: any) => ({
                    ...prev,
                    isThirdPerson: false,
                    firstName: "",
                    lastName: "",
                    kindOfDNI: "",
                    dni: "",
                    email: "",
                    phoneNumber: "",
                }));
            }
        }

        setErrors((prev: any) => ({
            ...prev,
            firstName: undefined,
            lastName: undefined,
            kindOfDNI: undefined,
            dni: undefined,
            email: undefined,
            phoneNumber: undefined,
        }));
    };

    const handleSave = async () => {
        if (!address) return;

        // Sync browser autofill values
        let syncedValues = { ...values };
        const modalElement = document.querySelector('.modal.show');
        if (modalElement) {
            const inputs = modalElement.querySelectorAll('input, select, textarea');
            let hasChanges = false;
            inputs.forEach((input: any) => {
                const name = input.getAttribute('name');
                if (name && input.value !== undefined && input.value !== (syncedValues as any)[name]) {
                    if (input.type === 'checkbox') {
                        (syncedValues as any)[name] = input.checked;
                    } else {
                        (syncedValues as any)[name] = input.value;
                    }
                    hasChanges = true;
                }
            });
            if (hasChanges) {
                if (syncedValues.phoneNumber) {
                    syncedValues.phoneNumber = formatPhoneNational(syncedValues.phoneNumber, selectedCountry, selectedCountryObj?.phoneCode);
                }
                if (syncedValues.dni) {
                    const selectedDniObj = findDniTypeByCompositeKey(dniTypes, syncedValues.kindOfDNI);
                    syncedValues.dni = applyDniPrefix(syncedValues.dni, selectedDniObj?.mask);
                }
                setValues(syncedValues);
            }
        }

        if (!validate(syncedValues)) {
            const fieldsToValidate = [
                "addressLine1",
                "city",
                "state",
                "zip",
                "nickname",
                "firstName",
                "lastName",
                "email",
                "phoneNumber",
                "kindOfDNI",
                "dni"
            ];
            let firstErrorField: string | null = null;
            for (const key of fieldsToValidate) {
                let isFieldInvalid = false;
                if (key === "addressLine1" && !syncedValues.addressLine1) isFieldInvalid = true;
                else if (key === "city" && !syncedValues.city) isFieldInvalid = true;
                else if (key === "state" && !syncedValues.state) isFieldInvalid = true;
                else if (key === "zip" && !syncedValues.zip) isFieldInvalid = true;
                else if (key === "nickname" && !syncedValues.nickname) isFieldInvalid = true;
                else if (key === "firstName" && !syncedValues.firstName) isFieldInvalid = true;
                else if (key === "lastName" && !syncedValues.lastName) isFieldInvalid = true;
                else if (key === "email") {
                    if (!syncedValues.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(syncedValues.email)) isFieldInvalid = true;
                }
                else if (key === "phoneNumber") {
                    if (!syncedValues.phoneNumber || !isValidPhone(syncedValues.phoneNumber, selectedCountry)) isFieldInvalid = true;
                }
                else if (key === "kindOfDNI" && !syncedValues.kindOfDNI) isFieldInvalid = true;
                else if (key === "dni") {
                    if (!syncedValues.dni) {
                        isFieldInvalid = true;
                    } else {
                        const selectedDniObj = findDniTypeByCompositeKey(dniTypes, syncedValues.kindOfDNI);
                        if (selectedDniObj && selectedDniObj.mask) {
                            const regex = new RegExp(selectedDniObj.mask);
                            if (!regex.test(syncedValues.dni)) {
                                isFieldInvalid = true;
                            }
                        }
                    }
                }

                if (isFieldInvalid) {
                    firstErrorField = key;
                    break;
                }
            }

            if (firstErrorField) {
                const element = document.getElementsByName(firstErrorField)[0] || document.getElementById(firstErrorField);
                if (element) {
                    (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
                    (element as HTMLElement).focus({ preventScroll: true });
                }
            }
            return;
        }

        if (!zipValidated) {
            const updatedZipData = await validateModalZip(syncedValues.zip);
            if (!updatedZipData) {
                return;
            }
            syncedValues = {
                ...syncedValues,
                ...updatedZipData,
            };
        }

        setIsSaving(true);
        try {
            const payload: Address = {
                id: address.id,
                addressLine1: syncedValues.addressLine1,
                addressLine2: syncedValues.addressLine2,
                city: syncedValues.city,
                state: syncedValues.state,
                zipCode: syncedValues.zip,
                isO2Country: selectedCountry,
                nickname: syncedValues.nickname || syncedValues.addressLine1,
                type: address.type,
                isDefault: activeType === "origin" ? !!syncedValues.isDefault : false,
            };

            const selectedCountryObj = countries.find((c) => c.iso2Country === selectedCountry);

            payload.addressSubjet = {
                id: address.addressSubjet?.id,
                idCustomerAddressBook: address.addressSubjet?.idCustomerAddressBook,
                firstName: syncedValues.firstName,
                lastName: syncedValues.lastName,
                kindOfDNI: findDniTypeByCompositeKey(dniTypes, syncedValues.kindOfDNI)?.kindOfDNI || syncedValues.kindOfDNI,
                dni: syncedValues.dni,
                phoneNumber: selectedCountryObj?.phoneCode
                    ? `${selectedCountryObj.phoneCode.startsWith("+") ? "" : "+"}${selectedCountryObj.phoneCode}${syncedValues.phoneNumber}`
                    : syncedValues.phoneNumber,
                email: syncedValues.email,
                isThirdPerson: activeType === "origin" ? !!syncedValues.isThirdPerson : false,
                razonSocial: findDniTypeByCompositeKey(dniTypes, syncedValues.kindOfDNI)?.requiereRazonSocial === true ? syncedValues.razonSocial : null,
            };

            const response = await customerAddressService.updateAddress(payload);
            if (response.success) {
                Swal.fire({
                    icon: "success",
                    title: "Éxito",
                    text: "Dirección actualizada correctamente.",
                });
                onSuccess();
                onClose();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.error || "No se pudo actualizar la dirección.",
                });
            }
        } catch (error) {
            console.error("Error updating address:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Ocurrió un error inesperado.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const isFormFilled = !!(
        values.nickname &&
        values.addressLine1 &&
        values.city &&
        values.state &&
        values.zip &&
        values.phoneNumber &&
        values.firstName &&
        values.lastName &&
        values.kindOfDNI &&
        values.dni &&
        values.email
    );

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            centered
            size="lg"
            className={styles.editAddressModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    {activeType === "origin"
                        ? "Editar Dirección de Origen"
                        : "Editar Dirección de Destino"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mb-4">
                    <Form.Group controlId="editModalCountrySelect">
                        <Form.Label style={{ fontWeight: 600, color: '#475569' }}>País</Form.Label>
                        <Form.Select
                            value={selectedCountry}
                            onChange={(e) => {
                                setSelectedCountry(e.target.value);
                                setValues((prev) => ({
                                    ...prev,
                                    addressLine1: "",
                                    addressLine2: "",
                                    city: "",
                                    state: "",
                                    zip: "",
                                    kindOfDNI: "", // Reset DNI type so it does not send stale values
                                }));
                                setZipValidated(false);
                                setZipCodeApiError(null);
                            }}
                            className={styles.editAddressModalInput}
                        >
                            <option value="">Seleccionar País</option>
                            {countries
                                .filter((c) => (activeType === "origin" ? c.send : c.receive))
                                .map((c) => (
                                    <option key={c.id} value={c.iso2Country}>
                                        {c.name}
                                    </option>
                                ))}
                        </Form.Select>
                    </Form.Group>
                </div>

                <p className={styles.modalSubtitle}>
                    Actualiza los datos de tu dirección guardada.
                </p>

                <Form
                    noValidate
                    onInput={(e: React.FormEvent<HTMLFormElement>) => {
                        const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
                        const name = target.name;
                        const value = target.value;
                        if (name) {
                            let val = value;
                            if (name === "phoneNumber") {
                                val = formatPhoneNational(value, selectedCountry, selectedCountryObj?.phoneCode);
                            } else if (name === "dni") {
                                const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.kindOfDNI);
                                val = applyDniPrefix(value, selectedDniObj?.mask);
                            }
                            setValues((prev) => {
                                if ((prev as any)[name] === val) return prev;
                                return {
                                    ...prev,
                                    [name]: val
                                };
                            });
                        }
                    }}
                >
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Group controlId="addressLine1">
                                <Form.Label>Dirección Línea 1</Form.Label>
                                {GOOGLE_API_KEY ? (
                                    <AddressAutocomplete
                                        value={values.addressLine1}
                                        onChange={(value) => setValues((prev) => ({ ...prev, addressLine1: value }))}
                                        onAddressSelect={handleAddressSelect}
                                        placeholder={activeType === "destination" && !selectedCountry ? "Selecciona un país primero" : "Calle, Avenida, Edificio..."}
                                        inputName="addressLine1"
                                        apiKey={GOOGLE_API_KEY}
                                        inputClassName={`${styles.editAddressModalInput} ${errors.addressLine1 ? styles.isInvalid : ""}`}
                                        country={country.toLowerCase()}
                                        disabled={activeType === "destination" && !selectedCountry}
                                        maxLength={150}
                                    />
                                ) : (
                                    <Form.Control
                                        type="text"
                                        name="addressLine1"
                                        value={values.addressLine1}
                                        onChange={handleChange}
                                        placeholder={activeType === "destination" && !selectedCountry ? "Selecciona un país primero" : "Calle, Avenida, Edificio..."}
                                        className={styles.editAddressModalInput}
                                        isInvalid={!!errors.addressLine1}
                                        disabled={activeType === "destination" && !selectedCountry}
                                        maxLength={150}
                                    />
                                )}
                                {errors.addressLine1 && (
                                    <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                        {errors.addressLine1}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="addressLine2">
                                <Form.Label>Dirección Línea 2 (Opcional)</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="addressLine2"
                                    value={values.addressLine2}
                                    onChange={handleChange}
                                    placeholder="Apto, Suite, Local..."
                                    className={styles.editAddressModalInput}
                                    maxLength={100}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="city">
                                <Form.Label>Ciudad</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="city"
                                    value={values.city}
                                    onChange={handleChange}
                                    placeholder="Ciudad"
                                    className={styles.editAddressModalInput}
                                    isInvalid={!!errors.city}
                                    maxLength={50}
                                />
                                 {errors.city && (
                                    <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                        {errors.city}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="state">
                                <Form.Label>Estado</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="state"
                                    value={values.state}
                                    onChange={handleChange}
                                    placeholder="Estado"
                                    className={styles.editAddressModalInput}
                                    isInvalid={!!errors.state}
                                    maxLength={50}
                                />
                                 {errors.state && (
                                    <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                        {errors.state}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>


                        <Col md={6}>
                            <Form.Group controlId="zipCode">
                                <Form.Label>Código Postal</Form.Label>
                                <div className={styles.zipInputContainer}>
                                    <Form.Control
                                        type="text"
                                        name="zip"
                                        value={values.zip}
                                        onChange={handleZipChange}
                                        onBlur={() => validateModalZip(values.zip)}
                                        placeholder="Ej: 33166"
                                        isInvalid={!!errors.zip || !!zipCodeApiError}
                                        className={styles.editAddressModalInput}
                                        maxLength={10}
                                    />
                                    {isValidatingZip && (
                                        <div className={styles.zipLoader}>
                                            <Spinner animation="border" size="sm" />
                                        </div>
                                    )}
                                     {(errors.zip || zipCodeApiError) && (
                                        <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                            {errors.zip || zipCodeApiError}
                                        </div>
                                    )}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group controlId="nickname">
                                <Form.Label>Alias</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nickname"
                                    value={values.nickname}
                                    onChange={handleChange}
                                    placeholder="Ej: Oficina Principal"
                                    className={styles.editAddressModalInput}
                                    isInvalid={!!errors.nickname}
                                    maxLength={50}
                                />
                                 {errors.nickname && (
                                    <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                        {errors.nickname}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        <>
                            <Col md={12} className="mt-4">
                                <div className={styles.modalDivider}>
                                    <span>
                                        {activeType === "origin"
                                            ? "Información del Remitente"
                                            : "Información del Receptor"}
                                    </span>
                                </div>
                            </Col>
                            {activeType === "origin" && (
                                <Col md={12} className="mt-2">
                                    <div
                                        className={`${styles.thirdPersonCheckboxWrapper} ${values.isThirdPerson ? styles.checked : ""}`}
                                        onClick={() => handleIsThirdPersonToggle(!values.isThirdPerson)}
                                    >
                                        <Form.Check
                                            type="checkbox"
                                            id="isThirdPerson"
                                            checked={!!values.isThirdPerson}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleIsThirdPersonToggle(e.target.checked);
                                            }}
                                            className={styles.thirdPersonCheckbox}
                                        />
                                        <div className={styles.thirdPersonLabel}>
                                            <span className={styles.thirdPersonLabelTitle}>
                                                ¿Es una tercera persona?
                                            </span>
                                            <span className={styles.thirdPersonLabelDesc}>
                                                Activa esta opción si el remitente no eres tú directamente.
                                            </span>
                                        </div>
                                    </div>
                                </Col>
                            )}
                            <>
                                <Col md={6}>
                                    <Form.Group controlId="firstName">
                                        <Form.Label>Nombre</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="firstName"
                                            value={values.firstName}
                                            onChange={handleChange}
                                            placeholder="Nombre"
                                            className={styles.editAddressModalInput}
                                            isInvalid={!!errors.firstName}
                                            maxLength={50}
                                        />
                                         {errors.firstName && (
                                            <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                                {errors.firstName}
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="lastName">
                                        <Form.Label>Apellido</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="lastName"
                                            value={values.lastName}
                                            onChange={handleChange}
                                            placeholder="Apellido"
                                            className={styles.editAddressModalInput}
                                            isInvalid={!!errors.lastName}
                                            maxLength={50}
                                        />
                                         {errors.lastName && (
                                            <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                                {errors.lastName}
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="email">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            placeholder="Email"
                                            className={styles.editAddressModalInput}
                                            isInvalid={!!errors.email}
                                            maxLength={100}
                                        />
                                         {errors.email && (
                                            <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                                {errors.email}
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </>
                            <Col md={6}>
                                <Form.Group controlId="phoneNumber">
                                    <Form.Label>Teléfono</Form.Label>
                                    <div
                                        className={`${styles.phoneInputWrapper} ${errors.phoneNumber ? styles.isInvalidWrapper : ""
                                            }`}
                                        style={!selectedCountry ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                                    >
                                        {selectedCountry && (
                                            <div className={styles.phonePrefix}>
                                                <img
                                                    src={`https://flagcdn.com/w40/${selectedCountry.toLowerCase()}.png`}
                                                    srcSet={`https://flagcdn.com/w80/${selectedCountry.toLowerCase()}.png 2x`}
                                                    width="20"
                                                    height="14"
                                                    alt={selectedCountry}
                                                    className={styles.phonePrefixFlag}
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                                {selectedCountryObj?.phoneCode && (
                                                    <span className={styles.phonePrefixCode}>
                                                        {selectedCountryObj.phoneCode.startsWith("+")
                                                            ? selectedCountryObj.phoneCode
                                                            : `+${selectedCountryObj.phoneCode}`}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <input
                                            type="text"
                                            name="phoneNumber"
                                            value={values.phoneNumber}
                                            onChange={handlePhoneChange}
                                            disabled={!selectedCountry}
                                            placeholder={!selectedCountry ? "Selecciona un país primero" : `Ej: ${getPhonePlaceholder(selectedCountry)}`}
                                            style={!selectedCountry ? { cursor: "not-allowed" } : {}}
                                            maxLength={15}
                                        />
                                    </div>
                                    {errors.phoneNumber && (
                                        <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                            {errors.phoneNumber}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <>
                                <Col md={6}>
                                    <Form.Group controlId="kindOfDNI">
                                        <Form.Label>Tipo Documento</Form.Label>
                                        <Form.Select
                                            name="kindOfDNI"
                                            value={values.kindOfDNI}
                                            onChange={handleKindOfDniChange}
                                            isInvalid={!!errors.kindOfDNI}
                                            className={styles.editAddressModalInput}
                                            disabled={loadingDniTypes || !selectedCountry}
                                        >
                                            <option value="">
                                                {loadingDniTypes ? "Cargando documentos..." : "Selecciona..."}
                                            </option>
                                            {!loadingDniTypes && dniTypes.map((t, i) => (
                                                <option key={getDniCompositeKey(t)} value={getDniCompositeKey(t)}>
                                                    {t.prefix || t.kindOfDNI}
                                                </option>
                                            ))}
                                        </Form.Select>
                                         {errors.kindOfDNI && (
                                            <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                                {errors.kindOfDNI}
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="dni">
                                        <Form.Label>Documento</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="dni"
                                            value={values.dni}
                                            onChange={handleDniChange}
                                            className={styles.editAddressModalInput}
                                            isInvalid={!!errors.dni}
                                            maxLength={getMaxLengthFromMask(findDniTypeByCompositeKey(dniTypes, values.kindOfDNI)?.mask) || 20}
                                        />
                                         {errors.dni && (
                                            <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                                {errors.dni}
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                {findDniTypeByCompositeKey(dniTypes, values.kindOfDNI)?.requiereRazonSocial === true && (
                                    <Col md={6}>
                                        <Form.Group controlId="razonSocial">
                                            <Form.Label>Razón Social</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="razonSocial"
                                                value={values.razonSocial}
                                                onChange={handleChange}
                                                className={styles.editAddressModalInput}
                                                isInvalid={!!errors.razonSocial}
                                                placeholder="Nombre de la empresa o razón social"
                                            />
                                         {errors.razonSocial && (
                                            <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                                                {errors.razonSocial}
                                            </div>
                                        )}
                                        </Form.Group>
                                    </Col>
                                )}
                            </>
                        </>
                        {activeType === "origin" && (
                            <Col md={12} className="mt-3">
                                <div
                                    className={`${styles.thirdPersonCheckboxWrapper} ${values.isDefault ? styles.checked : ""}`}
                                    onClick={() => setValues((prev: any) => ({ ...prev, isDefault: !prev.isDefault }))}
                                >
                                    <Form.Check
                                        type="checkbox"
                                        id="isDefault-checkbox-edit"
                                        checked={!!values.isDefault}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            setValues((prev: any) => ({ ...prev, isDefault: e.target.checked }));
                                        }}
                                        className={styles.thirdPersonCheckbox}
                                    />
                                    <div className={styles.thirdPersonLabel}>
                                        <span className={styles.thirdPersonLabelTitle}>
                                            Establecer como dirección de origen predeterminada
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={isSaving}
                    className={styles.secondaryBtn}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={
                        isSaving ||
                        isValidatingZip
                    }
                    className={`${modalStyles.closeBtn} ${styles.saveAddressBtnNew}`}
                >
                    {isSaving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />{" "}
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Guardar
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditAddressModal;
